import anylogger from 'anylogger';
import { throwAfter } from '@talismn/util';
import { ethers } from 'ethers';
import debounce from 'lodash/debounce';

const RPC_HEALTHCHECK_TIMEOUT = 10_000;
const RPC_CALL_TIMEOUT = 20_000;

var packageJson = {
	name: "@talismn/chain-connector-evm",
	version: "0.0.0-pr1020-20230901052420",
	author: "Talisman",
	homepage: "https://talisman.xyz",
	license: "GPL-3.0-or-later",
	publishConfig: {
		access: "public"
	},
	repository: {
		directory: "packages/chain-connector-evm",
		type: "git",
		url: "https://github.com/talismansociety/talisman.git"
	},
	main: "dist/talismn-chain-connector-evm.cjs.js",
	module: "dist/talismn-chain-connector-evm.esm.js",
	files: [
		"/dist"
	],
	engines: {
		node: ">=18"
	},
	scripts: {
		test: "jest",
		lint: "eslint src --max-warnings 0",
		clean: "rm -rf dist && rm -rf .turbo rm -rf node_modules"
	},
	dependencies: {
		"@talismn/chaindata-provider": "workspace:*",
		"@talismn/util": "workspace:*",
		anylogger: "^1.0.11",
		ethers: "5.7.2",
		lodash: "4.17.21"
	},
	devDependencies: {
		"@talismn/eslint-config": "workspace:*",
		"@talismn/tsconfig": "workspace:*",
		"@types/jest": "^27.5.1",
		"@types/lodash": "^4.14.180",
		eslint: "^8.4.0",
		jest: "^28.1.0",
		"ts-jest": "^28.0.2",
		typescript: "^4.6.4"
	},
	eslintConfig: {
		root: true,
		"extends": [
			"@talismn/eslint-config/base"
		]
	}
};

var log = anylogger(packageJson.name);

const BATCH_SIZE_LIMIT = 50; // requests
const BATCH_MAX_WAIT = 10; // milliseconds

class EvmJsonRpcBatchProvider extends ethers.providers.StaticJsonRpcProvider {
  queue = [];
  processQueue = debounce(this.sendBatch, BATCH_MAX_WAIT);
  sendBatch() {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0, BATCH_SIZE_LIMIT).map((req, index) => ({
      ...req,
      id: index + 1,
      jsonrpc: "2.0"
    }));
    const payload = batch.map(({
      method,
      params,
      id,
      jsonrpc
    }) => ({
      method,
      params,
      id,
      jsonrpc
    }));
    ethers.utils.fetchJson(this.connection, JSON.stringify(payload)).then(results => {
      if (!Array.isArray(results)) throw new Error("Invalid batch response");

      // order is not guaranteed, prepare a map to access items by their id
      const resultsMap = results.reduce((map, result) => map.set(result.id, result), new Map());
      batch.forEach(request => {
        const batchItem = resultsMap.get(request.id);
        if (!batchItem) return request.reject(new Error("Missing batch item"));
        const {
          result,
          error
        } = batchItem;
        if (error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const err = new Error(error.message);
          err.code = error.code;
          err.data = error.data;
          request.reject(err);
        } else {
          request.resolve(result);
        }
      });
    }).catch(err => {
      // emit error to cycle fallback provider
      this.emit("error", err);

      // reject all requests using a custom error message that will prevent more fallback cycling
      batch.forEach(request => {
        request.reject(new Error("BATCH_FAILED", {
          cause: err
        }));
      });
    });
  }
  send(method, params) {
    const request = {
      method,
      params
    };
    const result = new Promise((resolve, reject) => {
      // will be resolved/rejected from the sendBatch method
      request.resolve = resolve;
      request.reject = reject;
    });
    this.queue.push(request);

    // force batch to be processed if batch size is reached
    if (this.queue.length >= BATCH_SIZE_LIMIT) this.processQueue.flush();

    // call debounced batch processing anyway in case we're over the batch size limit
    this.processQueue();
    return result;
  }
}

/**
 * Helper function to add our onfinality api key to a public onfinality RPC url.
 */
const addOnfinalityApiKey = (rpcUrl, onfinalityApiKey) => {
  if (typeof onfinalityApiKey !== "string" || !onfinalityApiKey) return rpcUrl;

  // inject api key here because we don't want them in the store (user can modify urls of rpcs)
  return rpcUrl.replace(/^https:\/\/([A-z-]+)\.api\.onfinality\.io\/public-ws\/?$/, `https://$1.api.onfinality.io/ws?apikey=${onfinalityApiKey}`).replace(/^https:\/\/([A-z-]+)\.api\.onfinality\.io\/rpc\/?$/, `https://$1.api.onfinality.io/rpc?apikey=${onfinalityApiKey}`);
};
const isHealthyRpc = async (url, chainId) => {
  try {
    // StaticJsonRpcProvider is better suited for this as it will not do health check requests on it's own
    const provider = new ethers.providers.StaticJsonRpcProvider(url, {
      chainId,
      name: `EVM Network ${chainId}`
    });

    // check that RPC responds in time
    const rpcChainId = await Promise.race([provider.send("eth_chainId", []), throwAfter(RPC_HEALTHCHECK_TIMEOUT, "timeout")]);

    // with expected chain id
    return parseInt(rpcChainId, 16) === chainId;
  } catch (err) {
    log.error("Unhealthy EVM RPC %s", url, {
      err
    });
    return false;
  }
};
const getHealthyRpc = async (rpcUrls, network) => {
  for (const rpcUrl of rpcUrls) if (await isHealthyRpc(rpcUrl, network.chainId)) return rpcUrl;
  log.warn("No healthy RPC for EVM network %s (%d)", network.name, network.chainId);
  return null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isUnhealthyRpcError = err => {
  // expected errors that are not related to RPC health
  // ex : throw revert on a transaction call that fails
  if (err?.message === "BATCH_FAILED") return false;
  if (err?.reason === "processing response error") return false;

  // if unknown, assume RPC is unhealthy
  return true;
};
class StandardRpcProvider extends ethers.providers.JsonRpcProvider {
  async send(method, params) {
    try {
      return await super.send(method, params);
    } catch (err) {
      // emit error so rpc manager considers this rpc unhealthy
      if (isUnhealthyRpcError(err)) this.emit("error", err);
      throw err;
    }
  }
}
class BatchRpcProvider extends EvmJsonRpcBatchProvider {
  async send(method, params) {
    try {
      return await super.send(method, params);
    } catch (err) {
      // emit error so rpc manager considers this rpc unhealthy
      if (isUnhealthyRpcError(err)) this.emit("error", err);
      throw err;
    }
  }
}

const getEvmNetworkProviderCacheKey = (evmNetworkId, batch) => `id-${evmNetworkId}-${batch ? "batch" : "standard"}`;
const getUrlProviderCacheKey = (url, batch) => `url-${url}-${batch ? "batch" : "standard"}`;
class ChainConnectorEvm {
  #chaindataEvmNetworkProvider;
  #onfinalityApiKey;

  // cache for providers
  //
  // per network providers will change over time if they get unhealthy
  // per url providers do not change over time
  //
  // the cached object is a promise which will return the provider
  // if we didn't store the promise, multiple rpc calls sent before an rpc provider is ready
  // would end up in us creating multiple rpc providers, instead of what we want which is
  // to wait for the single provider to spin up
  #providerCache = new Map();

  // cache for rpc urls per network
  //
  // always initialized with the order defined in the database
  // when an error is raised, push the current rpc to the back of the list
  #rpcUrlsCache = new Map();
  constructor(chaindataEvmNetworkProvider, options) {
    this.#chaindataEvmNetworkProvider = chaindataEvmNetworkProvider;
    this.#onfinalityApiKey = options?.onfinalityApiKey ?? undefined;
  }
  setOnfinalityApiKey(apiKey) {
    this.#onfinalityApiKey = apiKey;
    this.clearRpcProvidersCache();
  }
  async getProviderForEvmNetworkId(evmNetworkId, {
    batch
  } = {}) {
    const network = await this.#chaindataEvmNetworkProvider.getEvmNetwork(evmNetworkId);
    if (!network) return null;
    return await this.getProviderForEvmNetwork(network, {
      batch
    });
  }
  async getProviderForEvmNetwork(evmNetwork, {
    batch
  } = {}) {
    const cacheKey = getEvmNetworkProviderCacheKey(evmNetwork.id, batch);

    // By using `Promise.race`, this variable will immediately resolve to either the
    // value of the promise at `this.#providerCache.get(cacheKey)`,
    // or if it's still pending, then it will resolve to `undefined`
    const cached = await Promise.race([this.#providerCache.get(cacheKey), undefined]);
    const createNewProvider =
    // Check if #providerCache has no pending provider for this key - if so, we should attempt to create a new provider
    !this.#providerCache.has(cacheKey) ||
    // Check if #providerCache has already resolved to `null` - if so, we should attempt to create a new provider
    cached === null;
    if (createNewProvider) {
      // store the promise straight away
      // otherwise another call to `getProviderForEvmNetwork` would create a new provider,
      // instead of what we want which is to wait for this provider to spin up
      this.#providerCache.set(cacheKey, this.newProviderFromEvmNetwork(evmNetwork, {
        batch
      }));
    }
    return (await this.#providerCache.get(cacheKey)) ?? null;
  }
  clearRpcProvidersCache(evmNetworkId, clearRpcUrlsCache = true) {
    if (evmNetworkId) {
      this.#providerCache.delete(getEvmNetworkProviderCacheKey(evmNetworkId, false));
      this.#providerCache.delete(getEvmNetworkProviderCacheKey(evmNetworkId, true));
      if (clearRpcUrlsCache) this.#rpcUrlsCache.delete(evmNetworkId);
    } else {
      this.#providerCache.clear();
      if (clearRpcUrlsCache) this.#rpcUrlsCache.clear();
    }
  }
  rotateRpcUrls(evmNetworkId) {
    const prevUrls = this.#rpcUrlsCache.get(evmNetworkId);
    if (!prevUrls || prevUrls.length < 2) return prevUrls;
    const nextUrls = prevUrls.slice(1).concat(prevUrls[0]);
    this.#rpcUrlsCache.set(evmNetworkId, nextUrls);
    return nextUrls;
  }
  async newProviderFromEvmNetwork(evmNetwork, {
    batch
  } = {}) {
    if (!Array.isArray(evmNetwork.rpcs)) return null;
    const network = {
      name: evmNetwork.name ?? "unknown network",
      chainId: parseInt(evmNetwork.id, 10)
    };

    // initialize cache for rpc urls if empty
    if (!this.#rpcUrlsCache.has(evmNetwork.id)) {
      const rpcUrls = evmNetwork.rpcs.map(({
        url
      }) => addOnfinalityApiKey(url, this.#onfinalityApiKey));
      this.#rpcUrlsCache.set(evmNetwork.id, rpcUrls);
    }
    let rpcUrls = this.#rpcUrlsCache.get(evmNetwork.id);
    const url = await getHealthyRpc(rpcUrls, network);
    if (!url) return null;

    // if healthy rpc url isn't the first one, rotate rpc urls cache to reflect that
    while (rpcUrls.includes(url) && rpcUrls[0] !== url) rpcUrls = this.rotateRpcUrls(evmNetwork.id);
    const urlCacheKey = getUrlProviderCacheKey(url, batch);
    if (!this.#providerCache.has(urlCacheKey)) {
      const connection = {
        url,
        errorPassThrough: true,
        timeout: RPC_CALL_TIMEOUT,
        // number of attempts when a request is throttled (429) : default is 12, minimum is 1
        // use 1 so we change RPC as soon as we detect a throttling, without trying again
        throttleLimit: 1
      };
      const provider = batch === true ? new BatchRpcProvider(connection, network) : new StandardRpcProvider(connection, network);

      // in case an error is thrown, rotate rpc urls cache
      // also clear provider cache to force logic going through getHealthyRpc again on next call
      provider.on("error", (...args) => {
        log.error("EVM RPC error %s (%s)", url, batch ? "batch" : "standard", args);
        this.rotateRpcUrls(evmNetwork.id);
        this.clearRpcProvidersCache(evmNetwork.id, false);
      });
      this.#providerCache.set(urlCacheKey, Promise.resolve(provider));
    }
    return (await this.#providerCache.get(urlCacheKey)) ?? null;
  }
}

export { ChainConnectorEvm };
